/**
 * Predictive Search - Shopify Native Search API
 * Replaces Sparq search with native Shopify predictive search
 */

(function() {
  'use strict';

  class PredictiveSearch {
    constructor(searchForm) {
      this.searchForm = searchForm;
      this.searchInput = searchForm.querySelector('input[type="search"], input[name="q"]');
      this.dropdown = null;
      this.config = null;
      this.abortController = null;
      this.debounceTimer = null;
      this.isOpen = false;
      this.selectedIndex = -1;
      this.results = [];
      this.minChars = 3;
      this.debounceMs = 300;

      if (!this.searchInput) return;

      this.init();
    }

    init() {
      // Load config from the page
      this.loadConfig();

      // Create and append dropdown
      this.createDropdown();

      // Bind events
      this.bindEvents();

      // Remove sparq wildcard behavior
      this.searchInput.removeAttribute('onchange');

      // Mark as initialized
      this.searchForm.setAttribute('data-predictive-search-initialized', 'true');
    }

    loadConfig() {
      const configEl = document.querySelector('[data-predictive-search-config]');
      if (configEl) {
        try {
          this.config = JSON.parse(configEl.textContent);
        } catch (e) {
          console.error('Failed to parse predictive search config:', e);
        }
      }

      // Fallback config
      if (!this.config) {
        this.config = {
          routes: { predictive_search_url: '/search/suggest' },
          strings: {
            products: 'Products',
            collections: 'Collections',
            pages: 'Pages',
            articles: 'Articles',
            view_all: 'View all results',
            no_results: 'No results found for'
          },
          money_format: '${{amount}}'
        };
      }
    }

    createDropdown() {
      // Check if dropdown already exists for this form
      let existingDropdown = this.searchForm.querySelector('.predictive-search');
      if (existingDropdown) {
        this.dropdown = existingDropdown;
        return;
      }

      // Create dropdown element
      this.dropdown = document.createElement('div');
      this.dropdown.className = 'predictive-search';
      this.dropdown.setAttribute('data-predictive-search', '');
      this.dropdown.setAttribute('aria-live', 'polite');
      this.dropdown.setAttribute('aria-busy', 'false');
      this.dropdown.innerHTML = `
        <div class="predictive-search__loading" aria-hidden="true">
          <svg class="predictive-search__spinner" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2" opacity="0.25"/>
            <path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
          </svg>
          <span>Searching...</span>
        </div>
        <div class="predictive-search__results" data-predictive-search-results></div>
        <div class="predictive-search__no-results" data-predictive-search-no-results hidden>
          <p>No results found</p>
        </div>
      `;

      // Insert after the search input wrapper
      const searchBox = this.searchInput.closest('.search_box') || this.searchInput.parentElement;
      searchBox.style.position = 'relative';
      searchBox.appendChild(this.dropdown);
    }

    bindEvents() {
      // Input events
      this.searchInput.addEventListener('input', this.onInput.bind(this));
      this.searchInput.addEventListener('focus', this.onFocus.bind(this));
      this.searchInput.addEventListener('keydown', this.onKeydown.bind(this));

      // Close on click outside
      document.addEventListener('click', this.onClickOutside.bind(this));

      // Close on escape
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this.close();
      });

      // Prevent form submit if dropdown is open and item selected
      this.searchForm.addEventListener('submit', this.onFormSubmit.bind(this));
    }

    onInput(event) {
      const query = event.target.value.trim();

      // Clear any pending search
      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer);
      }

      // Abort any in-flight request
      if (this.abortController) {
        this.abortController.abort();
      }

      // Don't search if query is too short
      if (query.length < this.minChars) {
        this.close();
        return;
      }

      // Debounce the search
      this.debounceTimer = setTimeout(() => {
        this.search(query);
      }, this.debounceMs);
    }

    onFocus() {
      const query = this.searchInput.value.trim();
      if (query.length >= this.minChars && this.results.length > 0) {
        this.open();
      }
    }

    onKeydown(event) {
      if (!this.isOpen) return;

      const items = this.dropdown.querySelectorAll('.predictive-search__item-link');

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          this.selectedIndex = Math.min(this.selectedIndex + 1, items.length - 1);
          this.updateSelection(items);
          break;

        case 'ArrowUp':
          event.preventDefault();
          this.selectedIndex = Math.max(this.selectedIndex - 1, -1);
          this.updateSelection(items);
          break;

        case 'Enter':
          if (this.selectedIndex >= 0 && items[this.selectedIndex]) {
            event.preventDefault();
            items[this.selectedIndex].click();
          }
          break;

        case 'Escape':
          this.close();
          break;
      }
    }

    updateSelection(items) {
      items.forEach((item, index) => {
        item.classList.toggle('is-selected', index === this.selectedIndex);
        if (index === this.selectedIndex) {
          item.scrollIntoView({ block: 'nearest' });
        }
      });
    }

    onClickOutside(event) {
      if (!this.searchForm.contains(event.target)) {
        this.close();
      }
    }

    onFormSubmit(event) {
      // Clean up any wildcard that might have been added
      let value = this.searchInput.value;
      if (value.endsWith('*')) {
        this.searchInput.value = value.slice(0, -1);
      }
    }

    async search(query) {
      this.showLoading();
      this.open();

      // Create new abort controller for this request
      this.abortController = new AbortController();

      try {
        const url = new URL(this.config.routes.predictive_search_url, window.location.origin);
        url.searchParams.set('q', query);
        url.searchParams.set('resources[type]', 'product,collection,page,article');
        url.searchParams.set('resources[limit]', '10');
        url.searchParams.set('resources[options][unavailable_products]', 'last');
        url.searchParams.set('resources[options][fields]', 'title,product_type,variants.title,variants.sku,vendor,tag');

        const response = await fetch(url.toString(), {
          signal: this.abortController.signal,
          headers: {
            'Accept': 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error(`Search failed: ${response.status}`);
        }

        const data = await response.json();
        this.renderResults(data, query);

      } catch (error) {
        if (error.name === 'AbortError') {
          // Request was cancelled, ignore
          return;
        }
        console.error('Predictive search error:', error);
        this.showNoResults(query);
      }
    }

    renderResults(data, query) {
      const resources = data.resources?.results || {};
      const resultsContainer = this.dropdown.querySelector('[data-predictive-search-results]');

      // Clear previous results
      resultsContainer.innerHTML = '';
      this.results = [];
      this.selectedIndex = -1;

      let hasResults = false;

      // Render products
      if (resources.products?.length > 0) {
        hasResults = true;
        this.results.push(...resources.products);
        resultsContainer.appendChild(this.renderSection(
          this.config.strings.products,
          resources.products,
          this.renderProduct.bind(this)
        ));
      }

      // Render collections
      if (resources.collections?.length > 0) {
        hasResults = true;
        this.results.push(...resources.collections);
        resultsContainer.appendChild(this.renderSection(
          this.config.strings.collections,
          resources.collections,
          this.renderCollection.bind(this)
        ));
      }

      // Render pages
      if (resources.pages?.length > 0) {
        hasResults = true;
        this.results.push(...resources.pages);
        resultsContainer.appendChild(this.renderSection(
          this.config.strings.pages,
          resources.pages,
          this.renderPage.bind(this)
        ));
      }

      // Render articles
      if (resources.articles?.length > 0) {
        hasResults = true;
        this.results.push(...resources.articles);
        resultsContainer.appendChild(this.renderSection(
          this.config.strings.articles,
          resources.articles,
          this.renderArticle.bind(this)
        ));
      }

      // Add view all link
      if (hasResults) {
        const viewAll = document.createElement('div');
        viewAll.className = 'predictive-search__view-all';
        viewAll.innerHTML = `
          <a href="/search?q=${encodeURIComponent(query)}" class="predictive-search__view-all-link">
            ${this.config.strings.view_all} (${query})
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M6 12l4-4-4-4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </a>
        `;
        resultsContainer.appendChild(viewAll);
      }

      this.hideLoading();

      if (!hasResults) {
        this.showNoResults(query);
      } else {
        this.hideNoResults();
      }
    }

    renderSection(title, items, renderItem) {
      const section = document.createElement('div');
      section.className = 'predictive-search__section';

      const header = document.createElement('h3');
      header.className = 'predictive-search__section-title';
      header.textContent = title;
      section.appendChild(header);

      const list = document.createElement('div');
      list.className = 'predictive-search__section-items';

      items.forEach(item => {
        list.appendChild(renderItem(item));
      });

      section.appendChild(list);
      return section;
    }

    renderProduct(product) {
      const item = document.createElement('div');
      item.className = 'predictive-search__item predictive-search__item--product';

      const imageUrl = product.image || product.featured_image?.url || '';
      const imageAlt = product.featured_image?.alt || product.title;
      const price = this.formatMoney(product.price);
      const comparePrice = product.compare_at_price_max > product.price
        ? this.formatMoney(product.compare_at_price_max)
        : '';

      item.innerHTML = `
        <a href="${product.url}" class="predictive-search__item-link">
          <div class="predictive-search__item-image">
            ${imageUrl ? `<img src="${this.resizeImage(imageUrl, '60x60')}" alt="${this.escapeHtml(imageAlt)}" loading="lazy" width="60" height="60">` : '<div class="predictive-search__item-placeholder"></div>'}
          </div>
          <div class="predictive-search__item-content">
            <p class="predictive-search__item-title">${this.highlightQuery(product.title)}</p>
            ${product.vendor ? `<p class="predictive-search__item-vendor">${this.escapeHtml(product.vendor)}</p>` : ''}
            <p class="predictive-search__item-price">
              ${comparePrice ? `<span class="predictive-search__item-compare-price">${comparePrice}</span>` : ''}
              <span class="${comparePrice ? 'predictive-search__item-sale-price' : ''}">${price}</span>
            </p>
          </div>
        </a>
      `;

      return item;
    }

    renderCollection(collection) {
      const item = document.createElement('div');
      item.className = 'predictive-search__item predictive-search__item--collection';

      const imageUrl = collection.image || collection.featured_image?.url || '';

      item.innerHTML = `
        <a href="${collection.url}" class="predictive-search__item-link">
          ${imageUrl ? `
            <div class="predictive-search__item-image">
              <img src="${this.resizeImage(imageUrl, '60x60')}" alt="${this.escapeHtml(collection.title)}" loading="lazy" width="60" height="60">
            </div>
          ` : ''}
          <div class="predictive-search__item-content">
            <p class="predictive-search__item-title">${this.highlightQuery(collection.title)}</p>
          </div>
        </a>
      `;

      return item;
    }

    renderPage(page) {
      const item = document.createElement('div');
      item.className = 'predictive-search__item predictive-search__item--page';

      item.innerHTML = `
        <a href="${page.url}" class="predictive-search__item-link">
          <div class="predictive-search__item-content">
            <p class="predictive-search__item-title">${this.highlightQuery(page.title)}</p>
          </div>
        </a>
      `;

      return item;
    }

    renderArticle(article) {
      const item = document.createElement('div');
      item.className = 'predictive-search__item predictive-search__item--article';

      const imageUrl = article.image || article.featured_image?.url || '';

      item.innerHTML = `
        <a href="${article.url}" class="predictive-search__item-link">
          ${imageUrl ? `
            <div class="predictive-search__item-image">
              <img src="${this.resizeImage(imageUrl, '60x60')}" alt="${this.escapeHtml(article.title)}" loading="lazy" width="60" height="60">
            </div>
          ` : ''}
          <div class="predictive-search__item-content">
            <p class="predictive-search__item-title">${this.highlightQuery(article.title)}</p>
            ${article.author ? `<p class="predictive-search__item-vendor">${this.escapeHtml(article.author)}</p>` : ''}
          </div>
        </a>
      `;

      return item;
    }

    // Utility methods
    formatMoney(cents) {
      if (typeof cents === 'string') {
        cents = cents.replace('.', '');
      }

      const amount = (parseInt(cents, 10) / 100).toFixed(2);
      return this.config.money_format
        .replace('{{amount}}', amount)
        .replace('{{amount_no_decimals}}', Math.round(amount))
        .replace('{{amount_with_comma_separator}}', amount.replace('.', ','));
    }

    resizeImage(url, size) {
      if (!url) return '';
      // Handle Shopify CDN image URLs
      if (url.includes('cdn.shopify.com')) {
        return url.replace(/(_\d+x\d+)?(\.[a-z]+)(\?.*)?$/i, `_${size}$2$3`);
      }
      return url;
    }

    escapeHtml(text) {
      const div = document.createElement('div');
      div.textContent = text || '';
      return div.innerHTML;
    }

    highlightQuery(text) {
      const query = this.searchInput.value.trim();
      if (!query || !text) return this.escapeHtml(text);

      const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const regex = new RegExp(`(${escapedQuery})`, 'gi');
      return this.escapeHtml(text).replace(regex, '<mark>$1</mark>');
    }

    showLoading() {
      this.dropdown.setAttribute('aria-busy', 'true');
      this.dropdown.querySelector('.predictive-search__loading').removeAttribute('hidden');
    }

    hideLoading() {
      this.dropdown.setAttribute('aria-busy', 'false');
      this.dropdown.querySelector('.predictive-search__loading').setAttribute('hidden', '');
    }

    showNoResults(query) {
      const noResults = this.dropdown.querySelector('[data-predictive-search-no-results]');
      noResults.innerHTML = `<p>${this.config.strings.no_results} "${this.escapeHtml(query)}"</p>`;
      noResults.removeAttribute('hidden');
    }

    hideNoResults() {
      this.dropdown.querySelector('[data-predictive-search-no-results]').setAttribute('hidden', '');
    }

    open() {
      this.isOpen = true;
      this.dropdown.classList.add('is-open');
      this.searchInput.setAttribute('aria-expanded', 'true');
    }

    close() {
      this.isOpen = false;
      this.dropdown.classList.remove('is-open');
      this.searchInput.setAttribute('aria-expanded', 'false');
      this.selectedIndex = -1;
    }
  }

  // Initialize on DOM ready
  function initPredictiveSearch() {
    const searchForms = document.querySelectorAll('form[action="/search"]:not([data-predictive-search-initialized])');
    searchForms.forEach(form => {
      new PredictiveSearch(form);
    });
  }

  // Run on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initPredictiveSearch);
  } else {
    initPredictiveSearch();
  }

  // Re-initialize if new forms are added dynamically
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (mutation.addedNodes.length) {
        initPredictiveSearch();
      }
    });
  });

  observer.observe(document.body, { childList: true, subtree: true });

  // Expose for external use
  window.PredictiveSearch = PredictiveSearch;
  window.initPredictiveSearch = initPredictiveSearch;
})();
